import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Grid from "@mui/material/Grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { useAppSelector } from "app/hooks";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import DateStringFormField from "components/date-string-form-field";
import InputFormField from "components/input-form-field";
import RadioGroupFormField from "components/radio-group-form-field";
import TimePicker from "components/time-picker";
import {
  COMMUNICATION_METHOD_EMAIL,
  COMMUNICATION_METHOD_VOICE_CALL,
  COMMUNICATION_METHOD_VOICE_DIRECT_MAIL
} from "constants/patients";
import { selectCurrentClinic } from "features/auth/auth-slice";
import type { Patient } from "services/types/patients.type";

import PatientFieldWithSearch from "../patient-field-with-search";
import { NewTouchpointFormValues } from "./new-touchpoint-form";
import { getCommunicationMethodOptions } from "./touchpoint-form-content.utils";
import TouchpointMessageField, {
  TouchpointMessageFieldRef
} from "./touchpoint-message-field.component";

type TouchpointFormContentProps = {
  defaultPatient: Patient | null;
  showMessageField: boolean;
  // When provided (e.g. prefilled from an Alert), seed "Assigned To" and keep
  // it until the user changes the communication method themselves.
  initialAssignedTo?: { label: string; value: string };
  // When provided (e.g. prefilled from an Alert), this communication method is
  // authoritative on open — the auto-select effect below must not re-derive and
  // clobber it (which briefly fell back to the first option / Voice Call).
  initialCommunicationMethod?: string | number | null;
  onPatientSelected?: () => void;
  onEmailSubjectChanged?: () => void;
};

const TouchpointFormContent = forwardRef<
  TouchpointMessageFieldRef,
  TouchpointFormContentProps
>(
  (
    {
      defaultPatient,
      showMessageField,
      initialAssignedTo,
      initialCommunicationMethod,
      onPatientSelected,
      onEmailSubjectChanged
    },
    ref
  ) => {
    const currentClinic = useAppSelector(selectCurrentClinic);

    // Honor a prefilled assignee (e.g. the alert's creator) over the default
    // comm-method auto-management, until the user picks a method themselves.
    const keepInitialAssignedToRef = useRef(Boolean(initialAssignedTo));

    // Honor a prefilled communication method (e.g. from an Alert) on open instead
    // of re-deriving it from the patient. Consumed once, on the first render where
    // the patient is present, so the auto-select effect never overrides the
    // prefilled value (which caused the flakey "defaults to Voice Call").
    const keepInitialCommMethodRef = useRef(initialCommunicationMethod != null);

    // The patient the method was last auto-selected for. The patient object's
    // identity is not a usable signal: react-hook-form hands back a fresh clone
    // of the same patient whenever a watched field updates, so keying the
    // auto-select on identity re-derived the preferred method over the one the
    // user had just clicked (DEV-3167). Key it on the id instead.
    const autoSelectedForPatientIdRef = useRef<number | null>(null);

    const { control, setValue } = useFormContext<NewTouchpointFormValues>();

    // useWatch, not watch(): right after the parent's reset() watch() still reads
    // the form's default values, so the method auto-selected below stayed
    // invisible here until the user's first interaction — which then looked like
    // a method change and cleared an assignee they had picked by hand.
    const [selectedPatient, communicationMethod] = useWatch<
      NewTouchpointFormValues,
      ["patient", "communication_method"]
    >({
      control,
      name: ["patient", "communication_method"]
    });

    const isVoiceCallOrDirectMail =
      Number(communicationMethod) === COMMUNICATION_METHOD_VOICE_CALL ||
      Number(communicationMethod) === COMMUNICATION_METHOD_VOICE_DIRECT_MAIL;

    const isEmail = Number(communicationMethod) === COMMUNICATION_METHOD_EMAIL;

    const communicationMethodOptions = useMemo(
      () => getCommunicationMethodOptions(selectedPatient),
      [selectedPatient]
    );

    useEffect(() => {
      if (selectedPatient) {
        // Only a different patient re-derives the method (see the ref above).
        if (autoSelectedForPatientIdRef.current === selectedPatient.id) return;
        autoSelectedForPatientIdRef.current = selectedPatient.id;

        // A prefilled method wins on open; consume the guard so any later
        // patient/method change the user makes still auto-selects normally.
        if (keepInitialCommMethodRef.current) {
          keepInitialCommMethodRef.current = false;
          return;
        }
        const preferredValue = communicationMethodOptions?.find(
          (option) =>
            Number(option.value) ===
              Number(selectedPatient?.preferred_communication_method) &&
            !option.disabled
        )?.value;

        const firstNotDisabledValue =
          communicationMethodOptions?.find((option) => !option.disabled)
            ?.value || null;

        setValue(
          "communication_method",
          preferredValue || firstNotDisabledValue
        );
      } else {
        autoSelectedForPatientIdRef.current = null;
        setValue("assigned_to", null);
        setValue("communication_method", null);
      }
    }, [setValue, selectedPatient, communicationMethodOptions]);

    // Primitives, not the patient object — see autoSelectedForPatientIdRef.
    const selectedPatientId = selectedPatient?.id;
    const preferredProviderId = selectedPatient?.preferred_provider?.id;
    const preferredProviderName = selectedPatient?.preferred_provider?.fullName;

    useEffect(() => {
      if (keepInitialAssignedToRef.current && initialAssignedTo) {
        setValue("assigned_to", initialAssignedTo);
        return;
      }
      if (isVoiceCallOrDirectMail && preferredProviderId != null) {
        setValue("assigned_to", {
          label: preferredProviderName,
          value: String(preferredProviderId)
        });
      } else {
        setValue("assigned_to", null);
      }
    }, [
      isVoiceCallOrDirectMail,
      setValue,
      communicationMethod,
      // A different patient still resets the assignee, as before.
      selectedPatientId,
      preferredProviderId,
      preferredProviderName,
      initialAssignedTo
    ]);

    return (
      <Grid container width="100%" gap={3}>
        {!defaultPatient && (
          <Grid item xs={12}>
            <PatientFieldWithSearch
              control={control}
              preferredClinic={currentClinic?.id}
              required
              onOptionSelected={onPatientSelected}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <RadioGroupFormField
            name="communication_method"
            label="Communication Method"
            row
            options={communicationMethodOptions}
            control={control}
            sx={{ mt: 0, mb: -1 }}
            required
            afterChange={() => {
              keepInitialAssignedToRef.current = false;
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectProvider
            isActive
            name="assigned_to"
            label="Assigned To"
            ignoreRegionsFilter
            ignoreClinicsFilter
            includeOtherClinics
            control={control}
            sx={{ ml: 0 }}
            required={isVoiceCallOrDirectMail}
          />
        </Grid>
        <Grid item xs={4}>
          <DateStringFormField
            name="execution_date"
            label="Scheduled Date"
            control={control}
            required
            sx={{ ml: 0, mt: 0 }}
          />
        </Grid>
        <Grid item xs={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              name="execution_time"
              label="Time *"
              control={control as any}
              openPicker={false}
            />
          </LocalizationProvider>
        </Grid>
        {isEmail && (
          <Grid item xs={12}>
            <InputFormField
              name="email_subject"
              label="Email Subject"
              control={control}
              sx={{ ml: 0 }}
              afterUpdate={onEmailSubjectChanged}
            />
          </Grid>
        )}
        {showMessageField && (
          <Grid item xs={12}>
            <TouchpointMessageField
              ref={ref}
              control={control}
              communicationMethod={communicationMethod}
              height={320}
              onMessageUpdate={(html) => {
                setValue("message", html, { shouldDirty: true });
              }}
              onSubjectUpdate={(subject) => {
                setValue("email_subject", subject, {
                  shouldDirty: true,
                  shouldValidate: true
                });
                onEmailSubjectChanged?.();
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <InputFormField
            name="notes"
            label="Notes"
            control={control}
            multiline
            sx={{ ml: 0 }}
          />
        </Grid>
      </Grid>
    );
  }
);

TouchpointFormContent.displayName = "TouchpointFormContent";

export default TouchpointFormContent;
